// Dashboard — "Atlas Standard" layout shell (US-874 · R-4, responsive).
//
// Desktop (md+): the mockup grid — rows `banner / header(56px) / content`,
// columns `rail(72px) · panel(420px) · map(1fr)`. The map is full-width; no right
// panel. Mobile (<md): a single column (banner / header / map) with the map
// full-bleed, and the rail + panel move into a slide-over Drawer opened from the
// header menu button — basic touch usability without a separate IA.
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Banner from "../components/banner/Banner";
import Header from "../components/header/Header";
import Rail from "../components/rail/Rail";
import SidePanel from "../components/panel/SidePanel";
import MapView from "../components/map/MapView";
import LegendOverlay from "../components/map/LegendOverlay";
import LandingGuide from "../components/map/LandingGuide";
import useUI from "../hooks/useUI";

// Corner anchor inside the map area; holds overlays/legend above the map canvas.
const MapChrome = ({ corner, position, children }) => (
  <Box
    data-chrome={corner}
    sx={{ position: "absolute", zIndex: 5, ...position }}
  >
    {children}
  </Box>
);

const Dashboard = () => {
  const theme = useTheme();
  const { layout, colors } = theme.tokens;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { mobileNavOpen, setMobileNavOpen } = useUI();

  return (
    <Box
      sx={{
        display: "grid",
        height: "100vh",
        bgcolor: "background.default",
        gridTemplateRows: `auto ${layout.headerH}px 1fr`,
        gridTemplateColumns: {
          xs: "1fr",
          md: `${layout.railW}px ${layout.panelW}px 1fr`,
        },
        gridTemplateAreas: {
          xs: `
            "banner"
            "header"
            "map"
          `,
          md: `
            "banner banner banner"
            "header header header"
            "rail panel map"
          `,
        },
      }}
    >
      <Banner />
      <Header onMenuClick={() => setMobileNavOpen(true)} />

      {/* Desktop: rail + panel live in the grid. Mobile: they live in the Drawer. */}
      {!isMobile && (
        <>
          <Rail />
          <SidePanel />
        </>
      )}

      {/* Full-width map area + corner chrome — US-875 map/layers/legend · US-876 anchors */}
      <Box
        component="main"
        aria-label="Map"
        data-region="map"
        sx={{
          gridArea: "map",
          position: "relative",
          overflow: "hidden",
          bgcolor: colors.mapSurface,
          zIndex: 1,
        }}
      >
        <MapView />
        {/* First-run wayfinding over the map (US-877); dismisses on interaction. */}
        <LandingGuide />
        <MapChrome corner="tl" position={{ top: 12, left: 12 }} />
        <MapChrome corner="tr" position={{ top: 12, right: 12 }} />
        <MapChrome corner="bl" position={{ bottom: 12, left: 12 }}>
          <LegendOverlay />
        </MapChrome>
        <MapChrome corner="br" position={{ bottom: 12, right: 12 }} />
      </Box>

      {/* Mobile slide-over: the same rail + panel, reachable from the header menu. */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          slotProps={{
            paper: {
              sx: { width: "min(440px, 92vw)", flexDirection: "row" },
            },
          }}
        >
          <Rail />
          <SidePanel />
        </Drawer>
      )}
    </Box>
  );
};

export default Dashboard;
